import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import type { StripeElementsOptions } from '@stripe/stripe-js';
import { getStripe, stripeConfigured } from '../lib/stripe';
import {
  createSetupIntent,
  recordCard,
  fetchMyPaymentMethod,
  fetchMyCards,
  fetchExistingCard,
  useExistingCardForBidding,
  fetchMyBidContact,
  saveBidContact,
  fetchMyShippingAddress,
  saveShippingAddress,
  fetchPostageQuote,
  type PostageQuote,
} from '../lib/queries';
import { COUNTRIES, flagEmoji } from '../lib/countries';
import { supabase } from '../lib/supabase';
import { useSession } from '../lib/session';
import { DIAL_CODES, buildE164 } from '../lib/dial-codes';
import './AddCardModal.css';

/**
 * "Register to bid" modal: contact details, then a delivery address (with a
 * live postage estimate for this lot, 067), then the card. The card step has
 * two paths, both gated behind a plain-language acknowledgement that winning
 * means being charged off-session:
 *   • Reuse - if the member already has a card on their Stripe Customer (e.g.
 *     from their subscription), offer one-click "use my card on file".
 *   • New card - collect one via Stripe Elements (Payment Element) on a
 *     SetupIntent. Card data stays inside Stripe's iframe.
 *
 * Stripe.js is loaded lazily (only when this modal mounts), so it isn't pulled
 * in app-wide. On success the webhook flips has_card_on_file; we briefly poll
 * for it so the bid panel unlocks without a manual refresh, then call onLinked().
 */

const ACK_TEXT =
  "I understand that if I win an auction, Converted Closet will charge this card for the " +
  "winning bid plus postage after the auction closes, without needing me to confirm again. " +
  "Postage shown while bidding is an estimate based on my delivery address; the exact " +
  "postage is confirmed before the charge.";

// Carriers reject these countries' addresses without a state/province;
// everywhere else the field is optional.
const STATE_REQUIRED = new Set(['US', 'CA', 'AU']);

const CONSENT_TEXT =
  "I agree to receive updates about the lots I bid on - outbid alerts, results, and payment - " +
  "by email and SMS. This is only for my own bidding activity; it is not marketing and I won't " +
  "be signed up for anything else.";

async function waitForCardOnFile() {
  for (let i = 0; i < 8; i++) {
    try {
      const pm = await fetchMyPaymentMethod();
      if (pm?.has_card_on_file) return;
    } catch { /* transient - keep polling */ }
    await new Promise((r) => setTimeout(r, 1500));
  }
}

// Adding an extra card: has_card_on_file is already true, so wait instead for
// the new card row to appear (the Stripe webhook records it just after confirm).
async function waitForNewCard(prevCount: number) {
  for (let i = 0; i < 8; i++) {
    try {
      const cards = await fetchMyCards();
      if (cards.length > prevCount) return;
    } catch { /* transient - keep polling */ }
    await new Promise((r) => setTimeout(r, 1500));
  }
}

export function AddCardModal({
  onClose, onLinked, postId, directNew = false,
}: {
  onClose: () => void;
  onLinked: () => void;
  /** The lot the member came from - used for the live postage estimate in the
   *  address step. Optional: without it the estimate is simply skipped. */
  postId?: string | null;
  /** Skip the register/address/reuse steps and go straight to a new-card form.
   *  Used when an already-registered member adds an extra card (CardBook) or
   *  replaces a declined one - they don't want the "use existing card" path. */
  directNew?: boolean;
}) {
  const overlayRef = useRef<HTMLDivElement>(null);
  // How many cards existed when we opened (directNew) - finish() waits for the
  // new one to land (the webhook records it a moment after Stripe confirms).
  const initialCardCount = useRef<number>(0);
  const { profile } = useSession();
  // Lazy: Stripe.js loads only now, when the modal is actually open.
  const stripePromise = useMemo(() => getStripe(), []);

  const [mode, setMode] = useState<'loading' | 'register' | 'address' | 'choose' | 'new'>('loading');
  const [existingCard, setExistingCard] = useState<{ brand: string | null; last4: string | null } | null>(null);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  // Prefill for the register step (name from profile, email from the account).
  const [defaults, setDefaults] = useState<{ name: string; email: string }>({ name: '', email: '' });

  // Body scroll lock + Esc close (mirrors SubmissionModal).
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);
  useEffect(() => {
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') { e.preventDefault(); onClose(); } }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const startNewCard = useCallback(() => {
    setMode('new');
    setClientSecret((cs) => {
      if (cs) return cs;
      createSetupIntent()
        .then(setClientSecret)
        .catch((err) => setLoadError(err?.message ?? 'Could not start card setup.'));
      return null;
    });
  }, []);

  // Move from the register step into the card step: reuse a card if one already
  // exists on the Stripe Customer, else go straight to the new-card form.
  const proceedToCard = useCallback(() => {
    setMode('loading');
    fetchExistingCard()
      .then((card) => {
        if (card?.last4) { setExistingCard(card); setMode('choose'); }
        else startNewCard();
      })
      .catch(() => startNewCard());
  }, [startNewCard]);

  // Whether the member already has a delivery address on file - decides
  // where the register step hands off to.
  const [hasAddress, setHasAddress] = useState(false);

  // On open: contact step first, then delivery address, then card, skipping
  // whatever is already on file.
  useEffect(() => {
    let cancelled = false;
    if (!stripeConfigured) { setLoadError('Card payments are not available right now.'); return; }
    // Adding an extra card / replacing one: skip register + address + the
    // "use existing card" reuse path, go straight to the new-card form.
    if (directNew) {
      fetchMyCards()
        .then((list) => { initialCardCount.current = list.length; })
        .catch(() => { /* count stays 0 */ })
        .finally(() => { if (!cancelled) startNewCard(); });
      return;
    }
    (async () => {
      const { data: sess } = await supabase.auth.getSession();
      const email = sess.session?.user?.email ?? '';
      if (cancelled) return;
      setDefaults({ name: profile?.display_name ?? '', email });
      try {
        const [contact, address] = await Promise.all([
          fetchMyBidContact(),
          fetchMyShippingAddress().catch(() => null),
        ]);
        if (cancelled) return;
        setHasAddress(!!address);
        if (!contact) setMode('register');
        else if (!address) setMode('address');
        else proceedToCard();
      } catch {
        if (!cancelled) setMode('register');
      }
    })();
    return () => { cancelled = true; };
  }, [proceedToCard, profile?.display_name, directNew, startNewCard]);

  const afterRegister = useCallback(() => {
    if (hasAddress) proceedToCard();
    else setMode('address');
  }, [hasAddress, proceedToCard]);

  const onBackdropClick = useCallback((e: React.MouseEvent) => {
    if (e.target === e.currentTarget) onClose();
  }, [onClose]);

  async function finish() {
    if (directNew) await waitForNewCard(initialCardCount.current);
    else await waitForCardOnFile();
    onLinked();
    onClose();
  }

  const options: StripeElementsOptions | undefined = clientSecret
    ? { clientSecret, appearance: { theme: 'flat', variables: { borderRadius: '10px' } } }
    : undefined;

  return (
    <div ref={overlayRef} className="addcard" role="dialog" aria-modal="true" aria-label="Register to bid" onClick={onBackdropClick}>
      <div className="addcard__panel">
        <button type="button" className="addcard__close" onClick={onClose} aria-label="Close">×</button>
        <p className="kicker addcard__kicker">Saleroom</p>
        <h2 className="addcard__title">
          {mode === 'register' ? 'Register to bid' : mode === 'address' ? 'Delivery address' : 'Add a card to bid'}
        </h2>
        <p className="addcard__lede">
          {mode === 'register'
            ? 'Three quick steps to bid: your contact details, a delivery address, then a card on file. You’re only charged if you win.'
            : mode === 'address'
            ? 'Where your wins get posted. We use this to estimate postage while you bid - the exact postage is confirmed before anything is charged.'
            : 'Bidding is a commitment, so we keep a card on file. You’re only charged if you win: the winning bid is settled after the auction closes.'}
        </p>

        {loadError && <p className="addcard__error" role="alert">{loadError}</p>}

        {!loadError && mode === 'loading' && <p className="addcard__loading">Loading…</p>}

        {!loadError && mode === 'register' && (
          <RegisterStep defaults={defaults} onSaved={afterRegister} onClose={onClose} />
        )}

        {!loadError && mode === 'address' && (
          <AddressStep
            defaultName={defaults.name}
            defaultCountry={profile?.country ?? ''}
            postId={postId ?? null}
            onSaved={proceedToCard}
            onClose={onClose}
          />
        )}

        {!loadError && mode === 'choose' && existingCard && (
          <ReuseChoice card={existingCard} onUseExisting={finish} onDifferent={startNewCard} onClose={onClose} />
        )}

        {!loadError && mode === 'new' && !options && <p className="addcard__loading">Loading secure card form…</p>}

        {!loadError && mode === 'new' && options && (
          <Elements stripe={stripePromise} options={options}>
            <CardForm onClose={onClose} onDone={finish} />
          </Elements>
        )}
      </div>
    </div>
  );
}

/** Step 1 of registering to bid: contact details + explicit consent to receive
 *  bid-activity updates. Saved before the card step. */
function RegisterStep({
  defaults, onSaved, onClose,
}: {
  defaults: { name: string; email: string };
  onSaved: () => void;
  onClose: () => void;
}) {
  const [name, setName] = useState(defaults.name);
  const [phoneCountry, setPhoneCountry] = useState('US');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState(defaults.email);
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const valid = name.trim() !== '' && phone.trim() !== '' && email.trim() !== '' && consent;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!valid || busy) return;
    // SMS alerts need a real E.164 number - country code from the selector
    // plus the national number (a pasted +… number wins over the selector).
    const normalizedPhone = buildE164(phoneCountry, phone);
    if (!normalizedPhone) {
      setError('That doesn’t look like a mobile number - check the country and the number.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await saveBidContact({ full_name: name.trim(), phone: normalizedPhone, email: email.trim(), consent: true });
      onSaved();
    } catch (err: any) {
      setError(err?.message ?? 'Could not save your details. Try again.');
      setBusy(false);
    }
  }

  return (
    <form className="addcard__form" onSubmit={submit}>
      <label className="addcard__field">
        <span className="addcard__field-label">Name</span>
        <input className="addcard__input" type="text" autoComplete="name" value={name}
               onChange={(e) => setName(e.target.value)} disabled={busy} required />
      </label>
      <div className="addcard__field">
        <span className="addcard__field-label">Mobile number</span>
        <div className="addcard__phone-row">
          <select
            className="addcard__input addcard__phone-country"
            value={phoneCountry}
            onChange={(e) => setPhoneCountry(e.target.value)}
            disabled={busy}
            aria-label="Phone country"
          >
            {COUNTRIES.filter((c) => DIAL_CODES[c.code]).map((c) => (
              <option key={c.code} value={c.code}>
                {flagEmoji(c.code)} {c.name} (+{DIAL_CODES[c.code]})
              </option>
            ))}
          </select>
          <input className="addcard__input addcard__phone-number" type="tel" inputMode="tel"
                 autoComplete="tel-national" value={phone} aria-label="Mobile number"
                 onChange={(e) => setPhone(e.target.value)} disabled={busy} required
                 placeholder="For bid updates by SMS" />
        </div>
      </div>
      <label className="addcard__field">
        <span className="addcard__field-label">Email</span>
        <input className="addcard__input" type="email" autoComplete="email" value={email}
               onChange={(e) => setEmail(e.target.value)} disabled={busy} required />
      </label>

      <label className="addcard__ack">
        <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)}
               aria-label="Consent to bid-activity updates" />
        <span>{CONSENT_TEXT}</span>
      </label>

      {error && <p className="addcard__error" role="alert">{error}</p>}

      <div className="addcard__actions">
        <button type="button" className="btn btn--ghost btn--sm" onClick={onClose} disabled={busy}>Cancel</button>
        <button type="submit" className="btn btn--primary btn--sm" disabled={!valid || busy}>
          {busy ? 'Saving…' : 'Continue'}
        </button>
      </div>
    </form>
  );
}

/** Step 2 of registering to bid: delivery address. After saving, shows the
 *  live postage estimate for the lot they came from (when the lot is
 *  rateable), with the "exact postage confirmed before charging" note -
 *  this is what the member consents to bidding against. */
function AddressStep({
  defaultName, defaultCountry, postId, onSaved, onClose,
}: {
  defaultName: string;
  defaultCountry: string;
  postId: string | null;
  onSaved: () => void;
  onClose: () => void;
}) {
  const [fullName, setFullName] = useState(defaultName);
  const [street1, setStreet1] = useState('');
  const [street2, setStreet2] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [country, setCountry] = useState(defaultCountry || 'US');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // After saving: show the estimate (when one came back) before the card step.
  const [phase, setPhase] = useState<'form' | 'estimate'>('form');
  const [quote, setQuote] = useState<PostageQuote | null>(null);

  const stateRequired = STATE_REQUIRED.has(country);
  const valid =
    fullName.trim() !== '' && street1.trim() !== '' && city.trim() !== '' &&
    postalCode.trim() !== '' && country !== '' && (!stateRequired || state.trim() !== '');

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!valid || busy) return;
    setBusy(true);
    setError(null);
    try {
      await saveShippingAddress({
        full_name: fullName.trim(),
        street1: street1.trim(),
        street2: street2.trim() === '' ? null : street2.trim(),
        city: city.trim(),
        state: state.trim() === '' ? null : state.trim(),
        postal_code: postalCode.trim(),
        country,
      });
      // Best-effort estimate for THIS lot; never blocks the flow.
      const q = postId ? await fetchPostageQuote(postId) : null;
      if (q) {
        setQuote(q);
        setPhase('estimate');
        setBusy(false);
      } else {
        onSaved();
      }
    } catch (err: any) {
      setError(err?.message ?? 'Could not save your address. Try again.');
      setBusy(false);
    }
  }

  if (phase === 'estimate' && quote) {
    return (
      <div className="addcard__form">
        <div className="addcard__estimate">
          <span className="addcard__estimate-label">Estimated postage to you</span>
          <strong className="addcard__estimate-amount">{dollars(quote.amount_cents)}</strong>
          {quote.service && (
            <span className="addcard__estimate-service">
              {quote.service}
              {quote.estimated_days != null && ` · ~${quote.estimated_days} day${quote.estimated_days === 1 ? '' : 's'}`}
            </span>
          )}
        </div>
        <p className="addcard__estimate-note">
          This is an estimate based on your address. If you win, the exact postage is
          confirmed before your card is charged - your total is always your winning
          bid plus postage.
        </p>
        <div className="addcard__actions">
          <button type="button" className="btn btn--ghost btn--md" onClick={() => setPhase('form')}>
            Edit address
          </button>
          <button type="button" className="btn btn--primary btn--md" onClick={onSaved}>
            Continue
          </button>
        </div>
      </div>
    );
  }

  return (
    <form className="addcard__form" onSubmit={submit}>
      <label className="addcard__field">
        <span className="addcard__field-label">Full name</span>
        <input className="addcard__input" type="text" autoComplete="shipping name" value={fullName}
               onChange={(e) => setFullName(e.target.value)} disabled={busy} required />
      </label>
      <label className="addcard__field">
        <span className="addcard__field-label">Street address</span>
        <input className="addcard__input" type="text" autoComplete="shipping address-line1" value={street1}
               onChange={(e) => setStreet1(e.target.value)} disabled={busy} required />
      </label>
      <label className="addcard__field">
        <span className="addcard__field-label">Apt / unit (optional)</span>
        <input className="addcard__input" type="text" autoComplete="shipping address-line2" value={street2}
               onChange={(e) => setStreet2(e.target.value)} disabled={busy} />
      </label>
      <div className="addcard__row">
        <label className="addcard__field">
          <span className="addcard__field-label">City</span>
          <input className="addcard__input" type="text" autoComplete="shipping address-level2" value={city}
                 onChange={(e) => setCity(e.target.value)} disabled={busy} required />
        </label>
        <label className="addcard__field">
          <span className="addcard__field-label">{stateRequired ? 'State' : 'State / region (optional)'}</span>
          <input className="addcard__input" type="text" autoComplete="shipping address-level1" value={state}
                 onChange={(e) => setState(e.target.value)} disabled={busy} required={stateRequired} />
        </label>
      </div>
      <div className="addcard__row">
        <label className="addcard__field">
          <span className="addcard__field-label">Postcode / ZIP</span>
          <input className="addcard__input" type="text" autoComplete="shipping postal-code" value={postalCode}
                 onChange={(e) => setPostalCode(e.target.value)} disabled={busy} required />
        </label>
        <label className="addcard__field">
          <span className="addcard__field-label">Country</span>
          <select className="addcard__input" autoComplete="shipping country" value={country}
                  onChange={(e) => setCountry(e.target.value)} disabled={busy} required>
            {COUNTRIES.map((c) => (
              <option key={c.code} value={c.code}>{c.name}</option>
            ))}
          </select>
        </label>
      </div>

      {error && <p className="addcard__error" role="alert">{error}</p>}

      <div className="addcard__actions">
        <button type="button" className="btn btn--ghost btn--md" onClick={onClose} disabled={busy}>Cancel</button>
        <button type="submit" className="btn btn--primary btn--md" disabled={!valid || busy}>
          {busy ? 'Saving…' : 'Continue'}
        </button>
      </div>
    </form>
  );
}

/** One-click reuse of a card already on the member's Stripe Customer. */
function ReuseChoice({
  card, onUseExisting, onDifferent, onClose,
}: {
  card: { brand: string | null; last4: string | null };
  onUseExisting: () => Promise<void>;
  onDifferent: () => void;
  onClose: () => void;
}) {
  const [ack, setAck] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function use() {
    if (!ack || busy) return;
    setBusy(true);
    setError(null);
    try {
      await useExistingCardForBidding();
      await onUseExisting();
    } catch (err: any) {
      setError(err?.message ?? 'Could not use that card. Try a different one.');
      setBusy(false);
    }
  }

  return (
    <div className="addcard__form">
      <div className="addcard__savedcard">
        <span className="addcard__savedcard-label">Card on file</span>
        <strong>{formatCard(card.brand, card.last4)}</strong>
      </div>

      <label className="addcard__ack">
        <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} aria-label="Acknowledge auction charge" />
        <span>{ACK_TEXT}</span>
      </label>

      {error && <p className="addcard__error" role="alert">{error}</p>}

      <div className="addcard__actions">
        <button type="button" className="btn btn--ghost btn--sm" onClick={onDifferent} disabled={busy}>
          Use a different card
        </button>
        <button type="button" className="btn btn--primary btn--sm" onClick={use} disabled={!ack || busy}>
          {busy ? 'Enabling…' : 'Use this card'}
        </button>
      </div>
      <button type="button" className="addcard__cancel-link" onClick={onClose} disabled={busy}>Cancel</button>
    </div>
  );
}

function CardForm({ onClose, onDone }: { onClose: () => void; onDone: () => Promise<void> }) {
  const stripe = useStripe();
  const elements = useElements();
  const [ack, setAck] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!stripe || !elements || !ack || busy) return;
    setBusy(true);
    setError(null);

    const { error: confirmErr, setupIntent } = await stripe.confirmSetup({
      elements,
      redirect: 'if_required', // stay in-app; SCA pops a modal only if needed
      confirmParams: { return_url: window.location.href },
    });

    if (confirmErr) {
      setError(confirmErr.message ?? 'That card could not be saved. Try another.');
      setBusy(false);
      return;
    }

    setConfirming(true);
    // Record the card now so it shows up immediately (webhook is the backstop).
    if (setupIntent?.id) {
      try { await recordCard(setupIntent.id); } catch { /* webhook will catch up */ }
    }
    await onDone();
  }

  return (
    <form className="addcard__form" onSubmit={handleSubmit}>
      <PaymentElement options={{ layout: 'tabs' }} />

      <label className="addcard__ack">
        <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} aria-label="Acknowledge auction charge" />
        <span>{ACK_TEXT}</span>
      </label>

      {error && <p className="addcard__error" role="alert">{error}</p>}

      <div className="addcard__actions">
        <button type="button" className="btn btn--ghost btn--sm" onClick={onClose} disabled={busy}>Cancel</button>
        <button type="submit" className="btn btn--primary btn--sm" disabled={!stripe || !ack || busy}>
          {confirming ? 'Finishing…' : busy ? 'Saving…' : 'Save card'}
        </button>
      </div>
    </form>
  );
}

function formatCard(brand: string | null, last4: string | null): string {
  const b = brand ? brand.charAt(0).toUpperCase() + brand.slice(1) : 'Card';
  return `${b} ···· ${last4 ?? '••••'}`;
}

function dollars(cents: number): string {
  return (cents / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}
